const KNOWN_ROUTES = new Set([
  '/',
  '/crm',
  '/mensajes',
  '/calendario',
  '/kanban',
  '/contratos',
  '/cotizaciones',
  '/finanzas',
  '/marketing',
  '/postventa',
  '/inteligencia',
  '/admin',
  '/portal',
  '/notificaciones',
]);

export const NOTIFICATION_MODULE_LABELS = {
  dashboard: 'Dashboard',
  crm: 'CRM',
  mensajes: 'Mensajes',
  calendario: 'Calendario',
  kanban: 'Proyectos',
  proyectos: 'Proyectos',
  contratos: 'Contratos',
  cotizaciones: 'Cotizaciones',
  finanzas: 'Finanzas',
  cobros: 'Finanzas',
  marketing: 'Marketing',
  postventa: 'Post-venta',
  inteligencia: 'Inteligencia',
  admin: 'Admin',
  sistema: 'Sistema',
  portal: 'Portal',
};

const PRIORITY_CONFIG = {
  critical: { label: 'Critica', color: 'var(--red)', rank: 4 },
  high: { label: 'Alta', color: 'var(--red)', rank: 3 },
  medium: { label: 'Media', color: 'var(--info)', rank: 2 },
  low: { label: 'Baja', color: 'var(--text-d)', rank: 1 },
  normal: { label: 'Normal', color: 'var(--text-d)', rank: 1 },
};

const SENSITIVE_METADATA_KEYS = new Set([
  'comprobante_path',
  'comprobante_url',
  'identity_document_path',
  'identity_document_url',
  'storage_path',
  'provider_payload',
  'raw_payload',
  'payload_raw',
  'contract_body',
  'contenido',
  'signature',
  'hash_firma',
]);

const asText = value => String(value || '').trim();

export function sanitizeNotificationMetadata(metadata = {}) {
  if (!metadata || typeof metadata !== 'object' || Array.isArray(metadata)) return {};
  return Object.fromEntries(
    Object.entries(metadata).filter(([key, value]) => {
      if (SENSITIVE_METADATA_KEYS.has(String(key))) return false;
      if (typeof value === 'string' && value.length > 500) return false;
      return value !== undefined;
    })
  );
}

export function notificationCategory(notification = {}) {
  const explicit = asText(notification.module || notification.categoria || notification.category).toLowerCase();
  if (explicit) return explicit;
  const raw = `${notification.tipo || ''} ${notification.titulo || ''} ${notification.title || ''} ${notification.descripcion || notification.desc || notification.body || ''}`.toLowerCase();
  if (raw.includes('mensaje') || raw.includes('whatsapp') || raw.includes('messenger') || raw.includes('instagram')) return 'mensajes';
  if (raw.includes('cobro') || raw.includes('pago') || raw.includes('transferencia') || raw.includes('stripe') || raw.includes('comprobante')) return 'finanzas';
  if (raw.includes('contrato') || raw.includes('firma')) return 'contratos';
  if (raw.includes('cotiz')) return 'cotizaciones';
  if (raw.includes('brief')) return 'proyectos';
  if (raw.includes('proyecto') || raw.includes('entrega') || raw.includes('kanban') || raw.includes('aprob')) return 'proyectos';
  if (raw.includes('meta') || raw.includes('webhook') || raw.includes('integracion') || raw.includes('integraci')) return 'admin';
  if (raw.includes('document') || raw.includes('identidad') || raw.includes('pdf')) return 'contratos';
  if (raw.includes('sistema') || raw.includes('error') || raw.includes('perfil')) return 'sistema';
  return asText(notification.tipo).toLowerCase() || 'general';
}

export function notificationModuleLabel(notification = {}) {
  const category = notificationCategory(notification);
  return NOTIFICATION_MODULE_LABELS[category] || NOTIFICATION_MODULE_LABELS[asText(notification.module).toLowerCase()] || category || 'General';
}

export function notificationRoute(notification = {}) {
  const explicit = asText(notification.route || notification.link || notification.href || notification.ruta);
  if (KNOWN_ROUTES.has(explicit)) return explicit;
  // ── UNA RUTA CONOCIDA CON PARAMETROS SIGUE SIENDO CONOCIDA ──────────────
  //
  // El Set compara la cadena entera, asi que `/mensajes?conv=<id>` no era
  // «conocida» y se descartaba entera: el aviso caia en `/mensajes` a secas y
  // David tenia que buscar la conversacion a mano, que es justo el trabajo que
  // la escalada existe para ahorrarle. Se valida la BASE contra la lista —la
  // parte que decide a donde se va— y se conserva la query.
  const base = explicit.split('?')[0].split('#')[0];
  if (base && KNOWN_ROUTES.has(base)) return explicit;
  const category = notificationCategory(notification);
  if (category === 'mensajes') return '/mensajes';
  if (category === 'finanzas' || category === 'cobros') return '/finanzas';
  if (category === 'contratos') return '/contratos';
  if (category === 'cotizaciones') return '/cotizaciones';
  if (category === 'briefs' || category === 'proyectos' || category === 'kanban') return '/kanban';
  if (category === 'admin' || category === 'sistema') return '/admin';
  return '';
}

export function notificationPriority(notification = {}) {
  const raw = asText(notification.priority || notification.prioridad || notification.tipo).toLowerCase();
  if (['critical', 'critica', 'critico', 'urgente'].some(token => raw.includes(token))) return PRIORITY_CONFIG.critical;
  if (['high', 'alta', 'urgent', 'venc', 'bloque'].some(token => raw.includes(token))) return PRIORITY_CONFIG.high;
  if (['medium', 'media', 'warning', 'pend', 'revision'].some(token => raw.includes(token))) return PRIORITY_CONFIG.medium;
  if (['low', 'baja'].some(token => raw.includes(token))) return PRIORITY_CONFIG.low;
  return PRIORITY_CONFIG.normal;
}

export function normalizeNotification(notification = {}) {
  const title = asText(notification.titulo || notification.title) || 'Notificacion interna';
  const body = asText(notification.descripcion || notification.desc || notification.body) || 'Sin descripcion adicional.';
  const normalized = {
    ...notification,
    titulo: title,
    title,
    descripcion: body,
    desc: body,
    module: asText(notification.module || notificationCategory(notification)) || null,
    priority: asText(notification.priority || notification.prioridad) || null,
    route: notificationRoute(notification) || null,
    metadata: sanitizeNotificationMetadata(notification.metadata || notification.payload || {}),
  };
  normalized.priorityConfig = notificationPriority(normalized);
  normalized.moduleLabel = notificationModuleLabel(normalized);
  return normalized;
}

export function isNotificationPending(notification = {}) {
  if (!notification || notification.leida || notification.read_at || notification.resolved_at || notification.dismissed_at) return false;
  if (notification.snoozed_until) {
    const snoozed = new Date(notification.snoozed_until);
    if (!Number.isNaN(snoozed.getTime()) && snoozed > new Date()) return false;
  }
  return true;
}

export function createInternalNotificationPayload(input = {}) {
  const now = input.created_at || new Date().toISOString();
  const title = asText(input.title || input.titulo) || 'Notificacion interna';
  const body = asText(input.body || input.descripcion || input.desc);
  const module = asText(input.module || input.source || notificationCategory(input)) || null;
  const priority = asText(input.priority || input.prioridad || 'normal').toLowerCase();
  const route = notificationRoute({ ...input, module });

  return {
    tipo: asText(input.tipo || input.type || module || 'interno'),
    titulo: title,
    descripcion: body,
    leida: input.leida ?? false,
    created_at: now,
    recipient_user_id: input.recipient_user_id || null,
    recipient_role: asText(input.recipient_role) || null,
    owner: asText(input.owner) || null,
    priority,
    module,
    entity_type: asText(input.entity_type) || null,
    entity_id: input.entity_id || null,
    route,
    dedupe_key: asText(input.dedupe_key) || null,
    metadata: sanitizeNotificationMetadata(input.metadata || {}),
    cliente_id: input.cliente_id || input.clienteId || null,
    proyecto_id: input.proyecto_id || input.proyectoId || null,
    channel: asText(input.channel || 'interno'),
    scheduled_for: input.scheduled_for || null,
    sent_at: input.sent_at || null,
    payload: sanitizeNotificationMetadata(input.payload || input.metadata || {}),
    is_demo: input.is_demo ?? false,
    tiempo: input.tiempo || null,
  };
}

export function formatNotificationDate(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}
